/**
 * Product analytics (PostHog) for the app.
 *
 * GA4 stays where it is and keeps answering "which campaign sent this traffic";
 * PostHog answers "what did they do next" — funnels, retention cohorts, session
 * replay. Events are `snake_case` and shared with the marketing site
 * (`web/src/analytics.js`), so renaming one here orphans that side's history too.
 *
 * Everything is a no-op when `VITE_POSTHOG_KEY` is unset (local dev, self-hosted
 * builds), and every call is wrapped: analytics must never break the app.
 */
import posthog from "posthog-js";

const ENABLED = import.meta.env.VITE_APP_POSTHOG_ENABLED !== "false";
const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || "https://eu.i.posthog.com";

let ready = false;

/** Coarse pointer or narrow viewport — the same test that gates voice sessions,
 *  so `is_mobile` in analytics means exactly "would hit the desktop-only wall". */
export function isMobile(): boolean {
  if (typeof window === "undefined") return false;
  return !!(
    window.matchMedia?.("(pointer: coarse)").matches ||
    window.matchMedia?.("(max-width: 820px)").matches
  );
}

export function initAnalytics(): void {
  if (!ENABLED || !KEY || ready) return;
  try {
    posthog.init(KEY, {
      api_host: HOST,
      // Every anonymous visitor gets a person profile: the whole point is tracing a
      // paying customer back to the pageview that started it, which needs the
      // anonymous → identified merge.
      person_profiles: "always",
      capture_pageview: true,
      // The privacy policy promises DNT is honoured — keep that true.
      respect_dnt: true,
      capture_pageleave: true,
      // Session replay is the reason PostHog is here at 10 signups — but never
      // record what people type, and never record a voice session's text input.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "[data-ph-mask]",
      },
    });
    ready = true;
  } catch (e) {
    /* analytics is never load-bearing */
  }
}

export function track(event: string, properties: Record<string, unknown> = {}): void {
  if (!ready) return;  // uninitialised — no key configured
  try {
    posthog.capture(event, properties);
  } catch {
    /* ignore */
  }
}

/**
 * Stitch the anonymous browsing history onto the signed-in user. `distinctId` is
 * the Clerk id — the same key the backend captures with (api/analytics.py), so
 * server-side `session_completed` / `checkout_completed` land on this person.
 */
export function identifyUser(distinctId: string, properties: Record<string, unknown> = {}): void {
  if (!ready || !distinctId) return;
  try {
    posthog.identify(distinctId, properties);
  } catch {
    /* ignore */
  }
}

/**
 * Referral attribution.
 *
 * `lib/referral.ts` owns the storage, the 60-day window and the PromoteKit
 * handoff — money depends on that value, so it has one owner. These two are the
 * analytics-side view of it: `ref` rides along as a person property, so every
 * later event is attributable to the partner who sent them.
 */
export { captureReferralFromUrl as captureRefFromUrl, readReferral as readRef } from "./referral";

/**
 * Session replay on/off, used by the conversation page.
 *
 * Replay uploads a chunk every few seconds for as long as the tab is open, so a
 * practice session — the longest-lived page in the app — is also the most
 * expensive one to record. It is also the one page whose rendered text is the
 * student's own speech and Elena's replies: `maskAllInputs` only covers form
 * fields, not transcript bubbles, so recording it would put conversation content
 * in PostHog. Both reasons point the same way — practice sessions are not
 * recorded.
 */
export function pauseSessionRecording(): void {
  if (!ready) return;
  try {
    posthog.stopSessionRecording();
  } catch {
    /* ignore */
  }
}

export function resumeSessionRecording(): void {
  if (!ready) return;
  try {
    posthog.startSessionRecording();
  } catch {
    /* ignore */
  }
}

export function resetAnalytics(): void {
  if (!ready) return;
  try {
    posthog.reset();
  } catch {
    /* ignore */
  }
}
